import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { usersService, idProofImageService } from '../services/api';
import { ROLES, PERMISSIONS, hasPermission, getRoleInfo, canDeleteRole } from '../utils/rbac';
import { validateAndCompressImage } from '../utils/imageCompression';
import { useIsMobile } from '../hooks';
import { AlertTriangle as AlertTriangleIcon, Ban as BanIcon, Calendar as CalendarIcon, Hourglass as HourglassIcon, Lightbulb as LightbulbIcon, Lock as LockIcon, Plus as PlusIcon, Trash2 as Trash2Icon, User as UserIcon, Users as UsersIcon } from 'lucide-react';

/**
 * User Management Component - Enhanced Version
 * Features:
 * - Pagination (100 users per page)
 * - ID Proof upload and view
 * - Search by email and name
 * - Role-based user creation
 * - Hierarchical role display
 * - Role management with validation
 */

const Colors = {
  primary: '#1A3E2A',
  secondary: '#5F6B61',
  success: '#1A3E2A',
  danger: '#A13F2F',
  warning: '#B68235',
  info: '#1A3E2A',
  white: '#ffffff',
  light: '#F5F3EB',
  border: '#E3DFD3',
  text: '#1A3E2A',
  textLight: '#5F6B61',
  hover: '#EEEBE1'
};

function UserManagementNew({ currentUser }) {
  const isMobile = useIsMobile();
  // State
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRole, setSelectedRole] = useState('ALL');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showIdProofModal, setShowIdProofModal] = useState(false);
  const [showIdProofViewModal, setShowIdProofViewModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [allowedRoles, setAllowedRoles] = useState([]);
  
  // Search & Pagination
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const pageSize = 100;

  // ID Proof
  const [idProofFile, setIdProofFile] = useState(null);
  const [idProofPreview, setIdProofPreview] = useState(null);
  const [uploadingIdProof, setUploadingIdProof] = useState(false);
  const [viewingIdProof, setViewingIdProof] = useState(null);

  // Delete Error Modal
  const [showDeleteErrorModal, setShowDeleteErrorModal] = useState(false);
  const [deleteErrorDetails, setDeleteErrorDetails] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    gender: 'OTHER',
    role: ROLES.GUEST
  });

  const [passwordData, setPasswordData] = useState({
    newPassword: ''
  });

  // Permission checks
  const canCreateUsers = hasPermission(currentUser?.role, PERMISSIONS.USER_CREATE);
  const canUpdateUsers = hasPermission(currentUser?.role, PERMISSIONS.USER_UPDATE);
  const canDeleteUsers = hasPermission(currentUser?.role, PERMISSIONS.USER_DELETE);

  // Role hierarchy (memoized to prevent re-creation)
  const roleHierarchy = React.useMemo(() => [
    ROLES.SUPERADMIN,
    ROLES.PROPERTYADMIN,
    ROLES.STAFF,
    ROLES.ACCOUNTS,
    ROLES.INVENTORY,
    ROLES.SERVICE,
    ROLES.GUEST
  ], []);

  // Load users
  const loadUsers = React.useCallback(async () => {
    setLoading(true);
    try {
      if (searchTerm.trim()) {
        const response = await usersService.search({
          email: searchTerm,
          name: searchTerm,
          role: selectedRole !== 'ALL' ? selectedRole : undefined,
          page: currentPage,
          limit: pageSize
        });
        
        const usersList = response?.data || [];
        setUsers(Array.isArray(usersList) ? usersList : []);
        
        if (response?.pagination) {
          setTotalPages(response.pagination.totalPages || 1);
          setTotalUsers(response.pagination.totalItems || usersList.length);
        } else {
          setTotalPages(1);
          setTotalUsers(usersList.length);
        }
      } else {
        const response = await usersService.getAll({
          page: currentPage,
          limit: pageSize,
          role: selectedRole !== 'ALL' ? selectedRole : undefined
        });
        
        const usersList = response?.data?.users || response?.data || response || [];
        setUsers(Array.isArray(usersList) ? usersList : []);
        
        if (response?.pagination) {
          setTotalPages(response.pagination.totalPages || 1);
          setTotalUsers(response.pagination.totalItems || usersList.length);
        } else {
          setTotalPages(1);
          setTotalUsers(usersList.length);
        }
      }
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error('Failed to load users');
      setUsers([]);
      setTotalPages(1);
      setTotalUsers(0);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, selectedRole, currentPage, pageSize]);

  // Load allowed roles (only once on mount)
  useEffect(() => {
    if (!canCreateUsers) return;
    
    const loadAllowedRoles = async () => {
      try {
        const response = await usersService.getAllowedRoles();
        const roles = response?.data?.allowedRoles || [];
        setAllowedRoles(roles);
      } catch (error) {
        console.error('Error loading allowed roles:', error);
        // Fallback to hardcoded roles based on user role
        if (currentUser?.role === ROLES.SUPERADMIN) {
          setAllowedRoles([
            ROLES.SUPERADMIN,
            ROLES.PROPERTYADMIN,
            ROLES.STAFF,
            ROLES.ACCOUNTS,
            ROLES.INVENTORY,
            ROLES.SERVICE,
            ROLES.GUEST
          ]);
        } else if (currentUser?.role === ROLES.PROPERTYADMIN) {
          setAllowedRoles([ROLES.PROPERTYADMIN, ROLES.STAFF, ROLES.ACCOUNTS, ROLES.INVENTORY, ROLES.SERVICE, ROLES.GUEST]);
        }
      }
    };

    loadAllowedRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedRole]);

  // Handlers
  const handleCreateUser = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.password) {
      toast.error('Name, email, and password are required');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    if (!allowedRoles.includes(formData.role)) {
      toast.error(`You don't have permission to create users with role: ${formData.role}`);
      return;
    }

    try {
      await usersService.createUser(formData);
      toast.success(`User created successfully with role: ${formData.role}`);
      setShowCreateModal(false);
      resetForm();
      loadUsers();
    } catch (error) {
      console.error('Error creating user:', error);
      const errorMsg = error.response?.data?.error || 'Failed to create user';
      toast.error(errorMsg);
    }
  };

  const handleUpdateRole = async (userId, newRole) => {
    if (userId === currentUser?.id) {
      toast.error('You cannot change your own role');
      return;
    }

    try {
      await usersService.updateUserRole(userId, newRole);
      toast.success('User role updated successfully');
      loadUsers();
    } catch (error) {
      console.error('Error updating role:', error);
      const errorMsg = error.response?.data?.error || 'Failed to update user role';
      toast.error(errorMsg);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();

    if (!passwordData.newPassword || passwordData.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    try {
      await usersService.resetUserPassword(editingUser.id, passwordData.newPassword);
      toast.success('Password reset successfully');
      setShowPasswordModal(false);
      setPasswordData({ newPassword: '' });
      setEditingUser(null);
    } catch (error) {
      console.error('Error resetting password:', error);
      const errorMsg = error.response?.data?.error || 'Failed to reset password';
      toast.error(errorMsg);
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();

    if (!formData.name) {
      toast.error('Name is required');
      return;
    }

    try {
      await usersService.updateUserProfile(editingUser.id, {
        name: formData.name,
        phone: formData.phone,
        gender: formData.gender
      });
      toast.success('Profile updated successfully');
      setShowEditModal(false);
      setEditingUser(null);
      resetForm();
      loadUsers();
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Failed to update profile');
    }
  };

  const handleDeleteUser = async (userId, targetUserRole, userName) => {
    // Check if trying to delete self
    if (userId === currentUser?.id) {
      toast.error('You cannot delete yourself');
      return;
    }

    // Check role hierarchy permissions
    if (!canDeleteRole(currentUser?.role, targetUserRole)) {
      toast.error(
        `You don't have permission to delete users with role: ${targetUserRole}. ` +
        'You can only delete users with lower access levels than your own.'
      );
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${userName || 'this user'}? This action cannot be undone.`)) {
      return;
    }

    try {
      await usersService.deleteUser(userId);
      toast.success('User deleted successfully');
      loadUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      handleDeleteError(error, userId, userName);
    }
  };

  // Handle delete error with detailed information
  const handleDeleteError = (error, userId, userName) => {
    const response = error.response?.data;
    const statusCode = error.response?.status;

    // Handle specific error cases
    if (statusCode === 400 && response?.details) {
      // User has related data - show detailed modal
      const details = response.details;
      const totalRelatedData = (details.bookings || 0) + (details.staffCreatedBookings || 0) + (details.availabilityBlocks || 0);
      
      setDeleteErrorDetails({
        userId,
        userName: userName || 'User',
        error: response.error || 'Cannot delete user with existing data',
        message: response.message,
        details: {
          bookings: details.bookings || 0,
          staffCreatedBookings: details.staffCreatedBookings || 0,
          availabilityBlocks: details.availabilityBlocks || 0,
          total: totalRelatedData
        }
      });
      setShowDeleteErrorModal(true);
    } else if (statusCode === 403) {
      // Forbidden - permission issue
      toast.error(
        response?.error || 
        'You don\'t have permission to delete this user. You can only delete users with lower access levels.'
      );
    } else if (statusCode === 404) {
      // User not found
      toast.error('User not found. They may have already been deleted.');
      loadUsers(); // Refresh the list
    } else {
      // Generic error
      const errorMsg = response?.error || response?.message || 'Failed to delete user. Please try again.';
      toast.error(errorMsg);
    }
  };

  // Handle deactivate user as alternative to deletion
  const handleDeactivateUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to deactivate ${userName}? They won't be able to log in, but their data will be preserved.`)) {
      return;
    }

    try {
      await usersService.toggleUserVerification(userId);
      toast.success(`User ${userName} has been deactivated successfully`);
      setShowDeleteErrorModal(false);
      setDeleteErrorDetails(null);
      loadUsers();
    } catch (error) {
      console.error('Error deactivating user:', error);
      toast.error('Failed to deactivate user. Please try again.');
    }
  };

  // Helper function to check if current user can delete a specific user
  const canDeleteUser = (targetUser) => {
    if (!canDeleteUsers) return false;
    if (targetUser.id === currentUser?.id) return false;
    return canDeleteRole(currentUser?.role, targetUser.role);
  };

  const handleIdProofFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }

      // Check if file is larger than 1MB and show warning
      const fileSizeMB = file.size / (1024 * 1024);
      if (fileSizeMB > 0.9) {
        toast.info(`File is ${fileSizeMB.toFixed(2)}MB. It will be automatically compressed before upload.`);
      }

      setIdProofFile(file);
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setIdProofPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUploadIdProof = async () => {
    if (!idProofFile) {
      toast.error('Please select a file to upload');
      return;
    }

    setUploadingIdProof(true);
    try {
      // Validate and compress the image (1MB limit to match server configuration)
      const { file: processedFile, wasCompressed, error: validationError } = await validateAndCompressImage(
        idProofFile,
        {
          maxSizeMB: 0.9, // 0.9MB to stay under 1MB server limit
          maxWidthOrHeight: 1920,
          quality: 0.8
        }
      );

      if (validationError || !processedFile) {
        toast.error(validationError || 'Failed to process image');
        setUploadingIdProof(false);
        return;
      }

      // Show compression notification
      if (wasCompressed) {
        toast.info(`Image compressed from ${(idProofFile.size / 1024).toFixed(0)}KB to ${(processedFile.size / 1024).toFixed(0)}KB`);
      }

      const formData = new FormData();
      formData.append('idProofImage', processedFile);

      await idProofImageService.upload(editingUser.id, formData);
      toast.success('ID Proof uploaded successfully');
      setShowIdProofModal(false);
      setIdProofFile(null);
      setIdProofPreview(null);
      setEditingUser(null);
      loadUsers();
    } catch (error) {
      console.error('Error uploading ID proof:', error);
      toast.error('Failed to upload ID proof');
    } finally {
      setUploadingIdProof(false);
    }
  };

  const handleViewIdProof = async (user) => {
    try {
      const imageUrl = await idProofImageService.getImageUrl(user.id);
      setViewingIdProof(imageUrl);
      setShowIdProofViewModal(true);
    } catch (error) {
      console.error('Error loading ID proof:', error);
      toast.error('Failed to load ID proof');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      phone: '',
      gender: 'OTHER',
      role: allowedRoles.length > 0 ? allowedRoles[allowedRoles.length - 1] : ROLES.GUEST
    });
  };

  const openEditModal = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name || '',
      email: user.email || '',
      password: '',
      phone: user.phone || '',
      gender: user.gender || 'OTHER',
      role: user.role
    });
    setShowEditModal(true);
  };

  const openPasswordModal = (user) => {
    setEditingUser(user);
    setPasswordData({ newPassword: '' });
    setShowPasswordModal(true);
  };

  const openIdProofModal = (user) => {
    setEditingUser(user);
    setIdProofFile(null);
    setIdProofPreview(null);
    setShowIdProofModal(true);
  };

  const getUserCountByRole = (role) => {
    return users.filter(user => user.role === role).length;
  };

  const canManageUser = (targetUser) => {
    if (!canUpdateUsers) return false;
    if (targetUser.id === currentUser?.id) return false;
    
    if (currentUser?.role === ROLES.SUPERADMIN) return true;
    
    if (currentUser?.role === ROLES.PROPERTYADMIN && targetUser.role === ROLES.SUPERADMIN) {
      return false;
    }
    
    return true;
  };

  const getManageableRolesForUser = (targetUser) => {
    if (currentUser?.role === ROLES.SUPERADMIN) {
      return roleHierarchy;
    }
    
    if (currentUser?.role === ROLES.PROPERTYADMIN) {
      return roleHierarchy.filter(role => role !== ROLES.SUPERADMIN);
    }
    
    return [];
  };

  // Pagination
  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePageClick = (page) => {
    setCurrentPage(page);
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 5;
    
    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push('...');
        pages.push(currentPage - 1);
        pages.push(currentPage);
        pages.push(currentPage + 1);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  // Render user card
  const renderUserCard = (user) => {
    const roleInfo = getRoleInfo(user.role);
    const canManage = canManageUser(user);
    const manageableRoles = getManageableRolesForUser(user);

    return (
      <div 
        key={user.id}
        style={{
          background: Colors.white,
          border: `1px solid ${Colors.border}`,
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '12px',
          transition: 'all 0.2s'
        }}
      >
        {/* User Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', flexDirection: isMobile ? 'column' : 'row', gap: '8px', marginBottom: '12px' }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
              <h4 style={{ margin: 0, color: Colors.text, fontSize: '16px', fontWeight: '600' }}>
                {user.name || 'Unnamed User'}
              </h4>
              {user.id === currentUser?.id && (
                <span style={{
                  background: Colors.primary,
                  color: Colors.white,
                  padding: '2px 8px',
                  borderRadius: '12px',
                  fontSize: '11px',
                  fontWeight: '500'
                }}>
                  You
                </span>
              )}
            </div>
            <p style={{ margin: '4px 0', color: Colors.textLight, fontSize: '14px' }}>
              {user.email}
            </p>
            {user.phone && (
              <p style={{ margin: '4px 0', color: Colors.textLight, fontSize: '14px' }}>
                {user.phone}
              </p>
            )}
          </div>

          <div style={{
            background: roleInfo.color,
            color: Colors.white,
            padding: '6px 12px',
            borderRadius: '6px',
            fontSize: '12px',
            fontWeight: '600',
            whiteSpace: 'nowrap'
          }}>
            {roleInfo.label}
          </div>
        </div>

        {/* User Info */}
        <div style={{ 
          display: 'flex', 
          gap: '12px', 
          flexWrap: 'wrap',
          marginBottom: '12px',
          paddingBottom: '12px',
          borderBottom: `1px solid ${Colors.border}`,
          fontSize: '13px',
          color: Colors.textLight
        }}>
          {user.gender && (
            <span>{user.gender === 'MALE' ? '' : user.gender === 'FEMALE' ? '' : ''} {user.gender}</span>
          )}
          <span>{user.id.substring(0, 8)}...</span>
        </div>

        {/* Actions */}
        {canManage && (
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', flexDirection: isMobile ? 'column' : 'row' }}>
            {manageableRoles.length > 0 && (
              <select
                value={user.role}
                onChange={(e) => handleUpdateRole(user.id, e.target.value)}
                style={{
                  padding: '6px 10px',
                  border: `1px solid ${Colors.border}`,
                  borderRadius: '6px',
                  fontSize: '13px',
                  cursor: 'pointer',
                  background: Colors.white,
                  color: Colors.text,
                  flex: isMobile ? 'unset' : 1,
                  minWidth: '120px'
                }}
              >
                {manageableRoles.map(role => {
                  const info = getRoleInfo(role);
                  return (
                    <option key={role} value={role}>
                      {info.label}
                    </option>
                  );
                })}
              </select>
            )}

            <button
              onClick={() => openEditModal(user)}
              style={{
                padding: '6px 12px',
                border: `1px solid ${Colors.border}`,
                borderRadius: '6px',
                fontSize: '13px',
                cursor: 'pointer',
                background: Colors.white,
                color: Colors.text,
                fontWeight: '500'
              }}
            >
              Edit
            </button>

            <button
              onClick={() => openPasswordModal(user)}
              style={{
                padding: '6px 12px',
                border: `1px solid ${Colors.warning}`,
                borderRadius: '6px',
                fontSize: '13px',
                cursor: 'pointer',
                background: Colors.white,
                color: Colors.warning,
                fontWeight: '500'
              }}
            >
              Reset
            </button>

            <button
              onClick={() => openIdProofModal(user)}
              style={{
                padding: '6px 12px',
                border: `1px solid ${Colors.info}`,
                borderRadius: '6px',
                fontSize: '13px',
                cursor: 'pointer',
                background: Colors.white,
                color: Colors.info,
                fontWeight: '500'
              }}
            >
              ID
            </button>

            {user.idProofImageUrl && (
              <button
                onClick={() => handleViewIdProof(user)}
                style={{
                  padding: '6px 12px',
                  border: `1px solid ${Colors.success}`,
                  borderRadius: '6px',
                  fontSize: '13px',
                  cursor: 'pointer',
                  background: Colors.white,
                  color: Colors.success,
                  fontWeight: '500'
                }}
              >
                View
              </button>
            )}

            {canDeleteUser(user) && (
              <button
                onClick={() => handleDeleteUser(user.id, user.role, user.name)}
                style={{
                  padding: '6px 12px',
                  border: `1px solid ${Colors.danger}`,
                  borderRadius: '6px',
                  fontSize: '13px',
                  cursor: 'pointer',
                  background: Colors.white,
                  color: Colors.danger,
                  fontWeight: '500'
                }}
                title={`Delete ${user.name}`}
              >
                <Trash2Icon size="1em" aria-hidden="true" />
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  // Render role tab
  const renderRoleTab = (role) => {
    const roleInfo = getRoleInfo(role);
    const count = getUserCountByRole(role);
    const isSelected = selectedRole === role;

    return (
      <button
        key={role}
        onClick={() => setSelectedRole(role)}
        style={{
          flex: '1',
          minWidth: '140px',
          padding: '12px 16px',
          border: isSelected ? `2px solid ${roleInfo.color}` : `1px solid ${Colors.border}`,
          borderRadius: '8px',
          background: isSelected ? roleInfo.color : Colors.white,
          color: isSelected ? Colors.white : Colors.text,
          cursor: 'pointer',
          fontWeight: isSelected ? '600' : '500',
          fontSize: '14px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '4px'
        }}
      >
        <span>{roleInfo.label}</span>
        <span style={{ fontSize: '12px', opacity: 0.9, fontWeight: '600' }}>
          {count} user{count !== 1 ? 's' : ''}
        </span>
      </button>
    );
  };

  // Render pagination
  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pageNumbers = getPageNumbers();

    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: isMobile ? 'stretch' : 'center',
        gap: '8px',
        marginTop: '24px',
        padding: isMobile ? '12px' : '16px',
        background: Colors.white,
        border: `1px solid ${Colors.border}`,
        borderRadius: '8px',
        flexWrap: 'wrap'
      }}>
        <button
          onClick={handlePreviousPage}
          disabled={currentPage === 1}
          style={{
            padding: '8px 12px',
            border: `1px solid ${Colors.border}`,
            borderRadius: '6px',
            background: currentPage === 1 ? Colors.light : Colors.white,
            color: currentPage === 1 ? Colors.textLight : Colors.text,
            cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
            fontWeight: '500',
            fontSize: '14px'
          }}
        >
          ← Prev
        </button>

        {pageNumbers.map((page, index) => {
          if (page === '...') {
            return (
              <span key={`ellipsis-${index}`} style={{ padding: '0 4px', color: Colors.textLight }}>
                ...
              </span>
            );
          }

          const isActive = page === currentPage;
          return (
            <button
              key={page}
              onClick={() => handlePageClick(page)}
              style={{
                padding: '8px 12px',
                border: `1px solid ${isActive ? Colors.primary : Colors.border}`,
                borderRadius: '6px',
                background: isActive ? Colors.primary : Colors.white,
                color: isActive ? Colors.white : Colors.text,
                cursor: 'pointer',
                fontWeight: isActive ? '600' : '500',
                fontSize: '14px',
                minWidth: '40px'
              }}
            >
              {page}
            </button>
          );
        })}

        <button
          onClick={handleNextPage}
          disabled={currentPage === totalPages}
          style={{
            padding: '8px 12px',
            border: `1px solid ${Colors.border}`,
            borderRadius: '6px',
            background: currentPage === totalPages ? Colors.light : Colors.white,
            color: currentPage === totalPages ? Colors.textLight : Colors.text,
            cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
            fontWeight: '500',
            fontSize: '14px'
          }}
        >
          Next →
        </button>

        <span style={{ marginLeft: '16px', color: Colors.textLight, fontSize: '14px' }}>
          Page {currentPage} of {totalPages}
        </span>
      </div>
    );
  };

  return (
    <div style={{ padding: isMobile ? '12px' : '20px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: isMobile ? 'stretch' : 'center',
        flexDirection: isMobile ? 'column' : 'row',
        marginBottom: '24px',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <div>
          <h2 style={{ margin: '0 0 8px 0', color: Colors.text, fontSize: isMobile ? '22px' : '24px', fontWeight: '700' }}>
            User Management
          </h2>
          <p style={{ margin: 0, color: Colors.textLight, fontSize: '14px' }}>
            Manage users and their roles • {totalUsers} total users
          </p>
        </div>

        {canCreateUsers && (
          <button
            onClick={() => {
              resetForm();
              setShowCreateModal(true);
            }}
            style={{
              padding: '10px 20px',
              background: Colors.primary,
              color: Colors.white,
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
              ,
              width: isMobile ? '100%' : 'auto',
              justifyContent: 'center'
            }}
          >
            <span style={{ fontSize: '16px' }}><PlusIcon size="1em" aria-hidden="true" /></span>
            Create New User
          </button>
        )}
      </div>

      {/* Search Bar */}
      <div style={{ marginBottom: '20px' }}>
        <input
          type="text"
          placeholder="Search by name or email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            width: '100%',
            padding: '12px 16px',
            border: `1px solid ${Colors.border}`,
            borderRadius: '8px',
            fontSize: '14px',
            boxSizing: 'border-box'
          }}
        />
      </div>

      {/* Role Tabs */}
      <div style={{ 
        display: 'flex', 
        gap: '12px', 
        marginBottom: '24px',
        overflowX: 'auto',
        paddingBottom: '8px',
        flexWrap: isMobile ? 'nowrap' : 'wrap'
      }}>
        <button
          onClick={() => setSelectedRole('ALL')}
          style={{
            flex: '1',
            minWidth: '140px',
            padding: '12px 16px',
            border: selectedRole === 'ALL' ? `2px solid ${Colors.primary}` : `1px solid ${Colors.border}`,
            borderRadius: '8px',
            background: selectedRole === 'ALL' ? Colors.primary : Colors.white,
            color: selectedRole === 'ALL' ? Colors.white : Colors.text,
            cursor: 'pointer',
            fontWeight: selectedRole === 'ALL' ? '600' : '500',
            fontSize: '14px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '4px'
          }}
        >
          <span style={{ fontSize: '20px' }}><UsersIcon size="1em" aria-hidden="true" /></span>
          <span>All Users</span>
          <span style={{ fontSize: '12px', opacity: 0.9, fontWeight: '600' }}>
            {totalUsers} user{totalUsers !== 1 ? 's' : ''}
          </span>
        </button>

        {roleHierarchy.map(role => renderRoleTab(role))}
      </div>

      {/* Users List */}
      {loading ? (
        <div style={{ 
          textAlign: 'center', 
          padding: '40px',
          color: Colors.textLight 
        }}>
          <div style={{ fontSize: '32px', marginBottom: '12px' }}><HourglassIcon size="1em" aria-hidden="true" /></div>
          Loading users...
        </div>
      ) : users.length === 0 ? (
        <div style={{
          background: Colors.light,
          border: `1px dashed ${Colors.border}`,
          borderRadius: '8px',
          padding: '40px',
          textAlign: 'center',
          color: Colors.textLight
        }}>
          <div style={{ fontSize: '48px', marginBottom: '12px' }}><UserIcon size="1em" aria-hidden="true" /></div>
          <p style={{ margin: 0, fontSize: '16px' }}>
            {searchTerm ? 'No users match your search' : 'No users found in this category'}
          </p>
        </div>
      ) : (
        <>
          <div>
            {users.map(user => renderUserCard(user))}
          </div>
          
          {renderPagination()}
        </>
      )}

      {/* Create User Modal */}
      {showCreateModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: Colors.white,
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <h3 style={{ margin: '0 0 20px 0' }}>Create New User</h3>
            <form onSubmit={handleCreateUser}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500' }}>Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  style={{ width: '100%', padding: '8px', border: `1px solid ${Colors.border}`, borderRadius: '4px', boxSizing: 'border-box' }}
                  required
                />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500' }}>Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  style={{ width: '100%', padding: '8px', border: `1px solid ${Colors.border}`, borderRadius: '4px', boxSizing: 'border-box' }}
                  required
                />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500' }}>Password *</label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  style={{ width: '100%', padding: '8px', border: `1px solid ${Colors.border}`, borderRadius: '4px', boxSizing: 'border-box' }}
                  required
                />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500' }}>Phone</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  style={{ width: '100%', padding: '8px', border: `1px solid ${Colors.border}`, borderRadius: '4px', boxSizing: 'border-box' }}
                />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500' }}>Gender</label>
                <select
                  value={formData.gender}
                  onChange={(e) => setFormData({...formData, gender: e.target.value})}
                  style={{ width: '100%', padding: '8px', border: `1px solid ${Colors.border}`, borderRadius: '4px' }}
                >
                  <option value="MALE">Male</option>
                  <option value="FEMALE">Female</option>
                  <option value="OTHER">Other</option>
                </select>
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500' }}>Role *</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({...formData, role: e.target.value})}
                  style={{ width: '100%', padding: '8px', border: `1px solid ${Colors.border}`, borderRadius: '4px' }}
                  required
                >
                  {allowedRoles.map(role => {
                    const info = getRoleInfo(role);
                    return <option key={role} value={role}>{info.label}</option>;
                  })}
                </select>
              </div>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  type="submit"
                  style={{
                    flex: 1,
                    padding: '10px',
                    background: Colors.primary,
                    color: Colors.white,
                    border: 'none',
                    borderRadius: '6px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Create User
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  style={{
                    flex: 1,
                    padding: '10px',
                    background: Colors.white,
                    color: Colors.text,
                    border: `1px solid ${Colors.border}`,
                    borderRadius: '6px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && editingUser && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: Colors.white,
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '500px',
            width: '90%'
          }}>
            <h3 style={{ margin: '0 0 20px 0' }}>Edit User Profile</h3>
            <form onSubmit={handleUpdateProfile}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500' }}>Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  style={{ width: '100%', padding: '8px', border: `1px solid ${Colors.border}`, borderRadius: '4px', boxSizing: 'border-box' }}
                  required
                />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500' }}>Phone</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  style={{ width: '100%', padding: '8px', border: `1px solid ${Colors.border}`, borderRadius: '4px', boxSizing: 'border-box' }}
                />
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500' }}>Gender</label>
                <select
                  value={formData.gender}
                  onChange={(e) => setFormData({...formData, gender: e.target.value})}
                  style={{ width: '100%', padding: '8px', border: `1px solid ${Colors.border}`, borderRadius: '4px' }}
                >
                  <option value="MALE">Male</option>
                  <option value="FEMALE">Female</option>
                  <option value="OTHER">Other</option>
                </select>
              </div>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  type="submit"
                  style={{
                    flex: 1,
                    padding: '10px',
                    background: Colors.primary,
                    color: Colors.white,
                    border: 'none',
                    borderRadius: '6px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Save Changes
                </button>
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  style={{
                    flex: 1,
                    padding: '10px',
                    background: Colors.white,
                    color: Colors.text,
                    border: `1px solid ${Colors.border}`,
                    borderRadius: '6px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Password Reset Modal */}
      {showPasswordModal && editingUser && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: Colors.white,
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '400px',
            width: '90%'
          }}>
            <h3 style={{ margin: '0 0 20px 0' }}>Reset Password</h3>
            <form onSubmit={handleResetPassword}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500' }}>New Password *</label>
                <input
                  type="password"
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({newPassword: e.target.value})}
                  style={{ width: '100%', padding: '8px', border: `1px solid ${Colors.border}`, borderRadius: '4px', boxSizing: 'border-box' }}
                  required
                />
              </div>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  type="submit"
                  style={{
                    flex: 1,
                    padding: '10px',
                    background: Colors.warning,
                    color: Colors.white,
                    border: 'none',
                    borderRadius: '6px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Reset Password
                </button>
                <button
                  type="button"
                  onClick={() => setShowPasswordModal(false)}
                  style={{
                    flex: 1,
                    padding: '10px',
                    background: Colors.white,
                    color: Colors.text,
                    border: `1px solid ${Colors.border}`,
                    borderRadius: '6px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ID Proof Upload Modal */}
      {showIdProofModal && editingUser && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: Colors.white,
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '500px',
            width: '90%'
          }}>
            <h3 style={{ margin: '0 0 20px 0' }}>Upload ID Proof</h3>
            <div style={{ marginBottom: '20px' }}>
              <input
                type="file"
                accept="image/*"
                onChange={handleIdProofFileChange}
                style={{ marginBottom: '16px' }}
              />
              {idProofPreview && (
                <img 
                  src={idProofPreview} 
                  alt="Preview" 
                  style={{ width: '100%', maxHeight: '300px', objectFit: 'contain', borderRadius: '8px' }}
                />
              )}
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={handleUploadIdProof}
                disabled={!idProofFile || uploadingIdProof}
                style={{
                  flex: 1,
                  padding: '10px',
                  background: Colors.primary,
                  color: Colors.white,
                  border: 'none',
                  borderRadius: '6px',
                  fontWeight: '600',
                  cursor: !idProofFile || uploadingIdProof ? 'not-allowed' : 'pointer',
                  opacity: !idProofFile || uploadingIdProof ? 0.6 : 1
                }}
              >
                {uploadingIdProof ? 'Uploading...' : 'Upload'}
              </button>
              <button
                onClick={() => setShowIdProofModal(false)}
                style={{
                  flex: 1,
                  padding: '10px',
                  background: Colors.white,
                  color: Colors.text,
                  border: `1px solid ${Colors.border}`,
                  borderRadius: '6px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ID Proof View Modal */}
      {showIdProofViewModal && viewingIdProof && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => setShowIdProofViewModal(false)}
        >
          <div style={{
            background: Colors.white,
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '800px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto'
          }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0 }}>ID Proof</h3>
              <button
                onClick={() => setShowIdProofViewModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: Colors.textLight
                }}
              >
                ×
              </button>
            </div>
            <img 
              src={viewingIdProof} 
              alt="ID Proof" 
              style={{ width: '100%', height: 'auto', borderRadius: '8px' }}
            />
          </div>
        </div>
      )}

      {/* Delete Error Modal - Shows details when user can't be deleted */}
      {showDeleteErrorModal && deleteErrorDetails && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.75)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}
          onClick={() => {
            setShowDeleteErrorModal(false);
            setDeleteErrorDetails(null);
          }}
        >
          <div style={{
            background: Colors.white,
            borderRadius: '16px',
            padding: '32px',
            maxWidth: '600px',
            width: '100%',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }} onClick={(e) => e.stopPropagation()}>
            
            {/* Header with icon */}
            <div style={{ 
              textAlign: 'center', 
              marginBottom: '24px',
              paddingBottom: '20px',
              borderBottom: `2px solid ${Colors.border}`
            }}>
              <div style={{ 
                fontSize: '56px', 
                marginBottom: '12px',
                animation: 'pulse 2s ease-in-out infinite'
              }}>
                <AlertTriangleIcon size="1em" aria-hidden="true" />
              </div>
              <h2 style={{ 
                margin: '0 0 8px 0', 
                color: Colors.danger,
                fontSize: '24px',
                fontWeight: '700'
              }}>
                Cannot Delete User
              </h2>
              <p style={{ 
                margin: 0, 
                color: Colors.textLight,
                fontSize: '14px'
              }}>
                {deleteErrorDetails.userName} has related data in the system
              </p>
            </div>

            {/* Error Message */}
            <div style={{ 
              background: '#F9EEEB',
              border: `1px solid #E5B3A9`,
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '24px'
            }}>
              <p style={{ 
                margin: '0 0 8px 0',
                color: '#5F241B',
                fontWeight: '600',
                fontSize: '14px'
              }}>
                {deleteErrorDetails.error}
              </p>
              <p style={{ 
                margin: 0,
                color: '#481B14',
                fontSize: '13px',
                lineHeight: '1.5'
              }}>
                {deleteErrorDetails.message}
              </p>
            </div>

            {/* Related Data Details */}
            <div style={{ marginBottom: '24px' }}>
              <h4 style={{ 
                margin: '0 0 16px 0',
                color: Colors.text,
                fontSize: '16px',
                fontWeight: '600'
              }}>
                Related Data Found:
              </h4>
              
              <div style={{ 
                display: 'grid',
                gap: '12px'
              }}>
                {deleteErrorDetails.details.bookings > 0 && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 16px',
                    background: Colors.light,
                    borderRadius: '8px',
                    border: `1px solid ${Colors.border}`
                  }}>
                    <span style={{ 
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      color: Colors.text,
                      fontSize: '14px',
                      fontWeight: '500'
                    }}>
                      <span style={{ fontSize: '20px' }}><CalendarIcon size="1em" aria-hidden="true" /></span>
                      Bookings as Guest
                    </span>
                    <span style={{
                      background: Colors.danger,
                      color: Colors.white,
                      padding: '4px 12px',
                      borderRadius: '12px',
                      fontSize: '13px',
                      fontWeight: '600'
                    }}>
                      {deleteErrorDetails.details.bookings}
                    </span>
                  </div>
                )}

                {deleteErrorDetails.details.staffCreatedBookings > 0 && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 16px',
                    background: Colors.light,
                    borderRadius: '8px',
                    border: `1px solid ${Colors.border}`
                  }}>
                    <span style={{ 
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      color: Colors.text,
                      fontSize: '14px',
                      fontWeight: '500'
                    }}>
                      <span style={{ fontSize: '20px' }}></span>
                      Bookings Created by Staff
                    </span>
                    <span style={{
                      background: Colors.warning,
                      color: Colors.white,
                      padding: '4px 12px',
                      borderRadius: '12px',
                      fontSize: '13px',
                      fontWeight: '600'
                    }}>
                      {deleteErrorDetails.details.staffCreatedBookings}
                    </span>
                  </div>
                )}

                {deleteErrorDetails.details.availabilityBlocks > 0 && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 16px',
                    background: Colors.light,
                    borderRadius: '8px',
                    border: `1px solid ${Colors.border}`
                  }}>
                    <span style={{ 
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      color: Colors.text,
                      fontSize: '14px',
                      fontWeight: '500'
                    }}>
                      <span style={{ fontSize: '20px' }}><BanIcon size="1em" aria-hidden="true" /></span>
                      Availability Blocks
                    </span>
                    <span style={{
                      background: Colors.info,
                      color: Colors.white,
                      padding: '4px 12px',
                      borderRadius: '12px',
                      fontSize: '13px',
                      fontWeight: '600'
                    }}>
                      {deleteErrorDetails.details.availabilityBlocks}
                    </span>
                  </div>
                )}

                {/* Total Summary */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '16px',
                  background: '#E1E9E2',
                  borderRadius: '8px',
                  border: `2px solid ${Colors.info}`,
                  marginTop: '8px'
                }}>
                  <span style={{ 
                    color: Colors.text,
                    fontSize: '15px',
                    fontWeight: '700'
                  }}>
                    Total Related Records
                  </span>
                  <span style={{
                    background: Colors.info,
                    color: Colors.white,
                    padding: '6px 16px',
                    borderRadius: '12px',
                    fontSize: '15px',
                    fontWeight: '700'
                  }}>
                    {deleteErrorDetails.details.total}
                  </span>
                </div>
              </div>
            </div>

            {/* Alternative Action Suggestion */}
            <div style={{
              background: '#F0F3EE',
              border: `1px solid #C4D4C8`,
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '24px'
            }}>
              <div style={{ 
                display: 'flex',
                alignItems: 'start',
                gap: '12px'
              }}>
                <span style={{ fontSize: '24px' }}><LightbulbIcon size="1em" aria-hidden="true" /></span>
                <div>
                  <p style={{ 
                    margin: '0 0 8px 0',
                    color: '#0F2619',
                    fontWeight: '600',
                    fontSize: '14px'
                  }}>
                    Recommended Alternative
                  </p>
                  <p style={{ 
                    margin: 0,
                    color: '#122E20',
                    fontSize: '13px',
                    lineHeight: '1.5'
                  }}>
                    Instead of deleting, you can <strong>deactivate</strong> this user account. 
                    This will prevent them from logging in while preserving all historical data 
                    and maintaining referential integrity.
                  </p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div style={{ 
              display: 'flex',
              gap: '12px',
              flexWrap: 'wrap'
            }}>
              <button
                onClick={() => {
                  handleDeactivateUser(deleteErrorDetails.userId, deleteErrorDetails.userName);
                }}
                style={{
                  flex: '1',
                  minWidth: '200px',
                  padding: '14px 24px',
                  background: Colors.warning,
                  color: Colors.white,
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  transition: 'all 0.2s ease'
                }}
                onMouseOver={(e) => e.target.style.opacity = '0.9'}
                onMouseOut={(e) => e.target.style.opacity = '1'}
              >
                <span style={{ fontSize: '16px' }}><LockIcon size="1em" aria-hidden="true" /></span>
                Deactivate User Instead
              </button>
              
              <button
                onClick={() => {
                  setShowDeleteErrorModal(false);
                  setDeleteErrorDetails(null);
                }}
                style={{
                  flex: '1',
                  minWidth: '150px',
                  padding: '14px 24px',
                  background: Colors.white,
                  color: Colors.text,
                  border: `2px solid ${Colors.border}`,
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseOver={(e) => {
                  e.target.style.background = Colors.light;
                  e.target.style.borderColor = Colors.secondary;
                }}
                onMouseOut={(e) => {
                  e.target.style.background = Colors.white;
                  e.target.style.borderColor = Colors.border;
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserManagementNew;